import { parseLetterAppointmentContext } from "../../../../../_shared/_logic/letter-table-tags"
import {
  parseWorkOrderPrintPreviewRow,
  parseWorkOrderLetterAddress,
  previewOptionalString,
  previewPrintMode,
  previewRecord,
  previewRequiredString,
  previewStringMap,
} from "../../../_shared/_logic/preview-context"
import type {
  LetterDeliveryDecisionPreviewPayload,
  LetterDeliveryDecisionPreviewRowContext,
  LetterDeliveryMode,
  LetterDeliveryModeOption,
  LetterDeliverySelection,
} from "../contract"

const PREVIEW_KEYS = new Set([
  "row_context_by_action_case_id",
  "row_errors_by_action_case_id",
  "shared",
])
const ROW_CONTEXT_KEYS = new Set([
  "appointment",
  "communication_presentation",
  "letter_delivery_batch_id",
  "letter_delivery_batch_item_id",
  "contact_order_step_id",
  "work_order_id",
  "schedule_event_id",
  "status",
  "print_preview_id",
  "selection",
  "delivery_mode_options",
])

export const LETTER_DELIVERY_DECISION_PREVIEW_KIND =
  "letter_delivery_batch.decide_appointment_letter_delivery.standard_table"

const deliveryMode = (value: unknown): LetterDeliveryMode | null =>
  value === "manual" || value === "automatic" ? value : null

const assertKeys = (source: Record<string, unknown>, allowed: readonly string[], path: string) => {
  if (Object.keys(source).some((key) => !allowed.includes(key))) {
    throw new Error(`${path} enthält unbekannte Felder.`)
  }
}

export const parseLetterDeliverySelection = (
  value: unknown,
  path = "selection",
): LetterDeliverySelection => {
  const source = previewRecord(value)
  const recipient = previewRecord(source.recipient)
  assertKeys(source, ["letter_delivery_batch_item_id", "print_mode", "recipient", "action"], path)
  const itemId = previewRequiredString(source.letter_delivery_batch_item_id)
  const mode = previewPrintMode(source.print_mode)
  const kind = previewRequiredString(recipient.kind)

  if (!itemId || !mode || !kind) throw new Error(`${path} ist unvollständig oder ungültig.`)

  if (kind === "existing_contact") {
    assertKeys(recipient, ["kind", "customer_id", "contact_point_id"], `${path}.recipient`)
    const customerId = previewRequiredString(recipient.customer_id)
    const contactPointId = previewRequiredString(recipient.contact_point_id)

    if (!customerId || !contactPointId) throw new Error(`${path}.recipient ist unvollständig.`)

    return {
      letter_delivery_batch_item_id: itemId,
      print_mode: mode,
      recipient: {
        kind,
        customer_id: customerId,
        contact_point_id: contactPointId,
      },
    }
  }

  if (kind === "customer_at_property_address") {
    assertKeys(recipient, ["kind", "customer_id", "name"], `${path}.recipient`)
    const customerId = previewRequiredString(recipient.customer_id)
    const name = previewOptionalString(recipient.name)

    if (!customerId || (recipient.name != null && !name)) {
      throw new Error(`${path}.recipient ist unvollständig.`)
    }

    return {
      letter_delivery_batch_item_id: itemId,
      print_mode: mode,
      recipient: {
        kind,
        customer_id: customerId,
        ...(name ? { name } : {}),
      },
    }
  }

  if (kind === "manual_address") {
    assertKeys(recipient, ["kind", "name", "address"], `${path}.recipient`)
    const name = previewRequiredString(recipient.name)
    const address = parseWorkOrderLetterAddress(recipient.address)

    if (!name || !address) throw new Error(`${path}.recipient ist unvollständig oder ungültig.`)

    return {
      letter_delivery_batch_item_id: itemId,
      print_mode: mode,
      recipient: { kind, name, address },
    }
  }

  if (kind === "property_address") {
    assertKeys(recipient, ["kind"], `${path}.recipient`)

    return {
      letter_delivery_batch_item_id: itemId,
      print_mode: mode,
      recipient: { kind },
    }
  }

  throw new Error(`${path}.recipient.kind ist ungültig.`)
}

const parseModeOption = (value: unknown, path: string): LetterDeliveryModeOption => {
  const source = previewRecord(value)
  assertKeys(source, ["mode", "label", "disabled_reason"], path)
  const mode = deliveryMode(source.mode)
  const label = previewRequiredString(source.label)
  const disabledReason = previewOptionalString(source.disabled_reason)

  if (!mode || !label || (source.disabled_reason != null && !disabledReason)) {
    throw new Error(`${path} ist unvollständig oder ungültig.`)
  }

  return {
    mode,
    label,
    disabled_reason: disabledReason,
  }
}

const parseRowContext = (value: unknown, path: string): LetterDeliveryDecisionPreviewRowContext => {
  const source = previewRecord(value)
  assertKeys(source, [...ROW_CONTEXT_KEYS], path)
  const batchId = previewRequiredString(source.letter_delivery_batch_id)
  const itemId = previewRequiredString(source.letter_delivery_batch_item_id)
  const contactOrderStepId = previewRequiredString(source.contact_order_step_id)
  const status = previewRequiredString(source.status)
  const printPreviewId = previewRequiredString(source.print_preview_id)
  const rawSelection = previewRecord(source.selection)
  const selection =
    rawSelection.action === "ignore"
      ? {
          action: "ignore" as const,
          letter_delivery_batch_item_id:
            previewRequiredString(rawSelection.letter_delivery_batch_item_id) || "",
        }
      : parseLetterDeliverySelection(source.selection, `${path}.selection`)

  if (
    !batchId ||
    !itemId ||
    !contactOrderStepId ||
    !status ||
    !printPreviewId ||
    selection.letter_delivery_batch_item_id !== itemId ||
    !Array.isArray(source.delivery_mode_options)
  ) {
    throw new Error(`${path} ist unvollständig oder ungültig.`)
  }

  const options = source.delivery_mode_options.map((entry, index) =>
    parseModeOption(entry, `${path}.delivery_mode_options.${index}`),
  )

  if (new Set(options.map((option) => option.mode)).size !== options.length) {
    throw new Error(`${path}.delivery_mode_options enthält doppelte Zustellarten.`)
  }

  const optionalString = (key: string) => {
    const parsed = previewOptionalString(source[key])

    if (source[key] != null && !parsed)
      throw new Error(`${path}.${key} muss String oder null sein.`)

    return parsed
  }

  return {
    ...parseLetterAppointmentContext(source),
    letter_delivery_batch_id: batchId,
    letter_delivery_batch_item_id: itemId,
    contact_order_step_id: contactOrderStepId,
    work_order_id: optionalString("work_order_id"),
    schedule_event_id: optionalString("schedule_event_id"),
    status,
    print_preview_id: printPreviewId,
    selection,
    delivery_mode_options: options,
  }
}

export const parseLetterDeliveryDecisionPreview = (
  value: unknown,
): LetterDeliveryDecisionPreviewPayload => {
  const source = previewRecord(value)

  if (Object.keys(source).some((key) => !PREVIEW_KEYS.has(key))) {
    throw new Error("Der Zustellart-Preview enthält unbekannte Felder.")
  }

  const rawContexts = previewRecord(source.row_context_by_action_case_id)
  const rowContextByActionCaseId: LetterDeliveryDecisionPreviewPayload["row_context_by_action_case_id"] =
    {}
  for (const [actionCaseId, context] of Object.entries(rawContexts)) {
    rowContextByActionCaseId[actionCaseId] = parseRowContext(
      context,
      `row_context_by_action_case_id.${actionCaseId}`,
    )
  }
  const shared = previewRecord(source.shared)

  if (Object.keys(shared).some((key) => key !== "print_previews_by_id")) {
    throw new Error("Der Zustellart-Preview enthält unerwartete Shared-Daten.")
  }

  const rawPrintPreviews = previewRecord(shared.print_previews_by_id)
  const printPreviewsById: LetterDeliveryDecisionPreviewPayload["shared"]["print_previews_by_id"] =
    {}
  for (const [previewId, preview] of Object.entries(rawPrintPreviews)) {
    const parsed = parseWorkOrderPrintPreviewRow(preview)

    if (!parsed) throw new Error(`shared.print_previews_by_id.${previewId} ist ungültig.`)

    printPreviewsById[previewId] = parsed
  }
  for (const [actionCaseId, context] of Object.entries(rowContextByActionCaseId)) {
    const printPreview = printPreviewsById[context.print_preview_id]

    if (!printPreview) {
      throw new Error(
        `row_context_by_action_case_id.${actionCaseId}.print_preview_id referenziert die fehlende ID ${context.print_preview_id}.`,
      )
    }

    if (context.work_order_id && printPreview.work_order_id !== context.work_order_id) {
      throw new Error(
        `row_context_by_action_case_id.${actionCaseId}.work_order_id passt nicht zur Druckvorschau.`,
      )
    }
  }

  return {
    row_context_by_action_case_id: rowContextByActionCaseId,
    row_errors_by_action_case_id: previewStringMap(source.row_errors_by_action_case_id),
    shared: { print_previews_by_id: printPreviewsById },
  }
}
